import React, { useState } from "react";
import { FiGlobe, FiMail, FiMapPin, FiSave, FiX } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import { WhatsAppCategory } from "../../types/MetaProfileType";

type BusinessProfileModalProps = {
  categories: WhatsAppCategory[];
  category: string;
  setCategory: (value: string) => void;
  about: string;
  setAbout: (value: string) => void;
  description: string;
  setDescription: (value: string) => void;
  address: string;
  setAddress: (value: string) => void;
  email: string;
  setEmail: (value: string) => void;
  website1: string;
  setWebsite1: (value: string) => void;
  website2: string;
  setWebsite2: (value: string) => void;
  onClose: () => void;
};

const inputClass =
  "h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-xs text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-brand-main focus:ring-2 focus:ring-brand-main/10";

export const BusinessProfileModal = ({
  categories,
  category,
  setCategory,
  about,
  setAbout,
  description,
  setDescription,
  address,
  setAddress,
  email,
  setEmail,
  website1,
  setWebsite1,
  website2,
  setWebsite2,
  onClose,
}: BusinessProfileModalProps) => {
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      await new Promise((resolve) => setTimeout(resolve, 800));
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 p-3 backdrop-blur-sm sm:p-5"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) {
          onClose();
        }
      }}
    >
    
      <div className="flex h-full max-h-[80vh] w-full max-w-[950px] overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* LEFT PANEL */}
        <aside className="relative hidden w-[38%] shrink-0 flex-col justify-between overflow-hidden bg-brand-blue-gradient p-8 md:flex">
        
          <div className="absolute -right-12 -top-12 h-44 w-44 rounded-full bg-white/10 blur-xl" />
          <div className="absolute -bottom-16 -left-16 h-52 w-52 rounded-full bg-white/10 blur-2xl" />

          {/* Top Content */}
          <div className="relative z-10">
            <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-md">
              <FaWhatsapp className="h-6 w-6 text-white" />
            </div>

            <h2 className="text-2xl font-extrabold leading-tight tracking-tight text-white">
              Business Profile
            </h2>

            <p className="mt-3 text-xs leading-5 text-white/80">
              Manage the information customers see on your WhatsApp business profile.
            </p>
          </div>

          {/* Bottom Info */}
          <div className="relative z-10">
            <div className="mb-4 h-px bg-white/20" />
            <p className="text-xs font-semibold text-white/90">
              WhatsApp Business API
            </p>
            <p className="mt-1 text-[11px] leading-4 text-white/60">
              Some changes may require Meta approval.
            </p>
          </div>
        </aside>

        {/* RIGHT FORM */}
        <form onSubmit={handleSubmit} className="flex h-full min-h-0 flex-1 flex-col">
          {/* HEADER */}
          <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-6 py-4">
            <div>
              <h2 className="text-sm font-bold text-brand-secondary">
                Edit business profile
              </h2>
              <p className="mt-0.5 text-[11px] text-text-secondary">
                Update the information shown to your customers
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
              aria-label="Close"
            >
              <FiX className="h-4 w-4" />
            </button>
          </div>

          {/* BODY */}
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
            <div className="space-y-6">
              {/* BUSINESS INFO */}
              <FormSection
                title="Business information"
                description="Basic information about your business"
              >
                <FormField label="Category">
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className={`${inputClass} cursor-pointer`}
                  >
                    {categories.map((cat) => (
                      <option key={cat.code} value={cat.code}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </FormField>

                <FormField label="About" hint={`${about.length}/139`}>
                  <input
                    type="text"
                    maxLength={139}
                    value={about}
                    onChange={(e) => setAbout(e.target.value)}
                    placeholder="Hey there! I am using WhatsApp."
                    className={inputClass}
                  />
                </FormField>

                <FormField label="Description" hint={`${description.length}/512`}>
                  <textarea
                    maxLength={512}
                    rows={4}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Tell customers about your business..."
                    className={`${inputClass} h-auto min-h-[100px] resize-none py-3`}
                  />
                </FormField>
              </FormSection>

              {/* CONTACT INFO */}
              <FormSection
                title="Contact information"
                description="Optional contact details customers can see"
              >
                <FormField label="Address">
                  <IconInput icon={<FiMapPin />}>
                    <input
                      type="text"
                      maxLength={256}
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="Enter business address"
                      className={inputClass}
                    />
                  </IconInput>
                  <CharacterCount current={address.length} max={256} />
                </FormField>

                <FormField label="Email">
                  <IconInput icon={<FiMail />}>
                    <input
                      type="email"
                      maxLength={128}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="business@example.com"
                      className={inputClass}
                    />
                  </IconInput>
                  <CharacterCount current={email.length} max={128} />
                </FormField>
              </FormSection>

              {/* WEBSITES */}
              <FormSection
                title="Websites"
                description="Add up to two websites to your profile"
              >
                <FormField label="Website 1">
                  <IconInput icon={<FiGlobe />}>
                    <input
                      type="url"
                      maxLength={256}
                      value={website1}
                      onChange={(e) => setWebsite1(e.target.value)}
                      placeholder="https://example.com"
                      className={inputClass}
                    />
                  </IconInput>
                </FormField>

                <FormField label="Website 2">
                  <IconInput icon={<FiGlobe />}>
                    <input
                      type="url"
                      maxLength={256}
                      value={website2}
                      onChange={(e) => setWebsite2(e.target.value)}
                      placeholder="https://www.example.com"
                      className={inputClass}
                    />
                  </IconInput>
                </FormField>
              </FormSection>
            </div>
          </div>

          {/* FOOTER */}
          <div className="shrink-0 border-t border-gray-100 bg-white px-6 py-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-[10px] leading-4 text-text-secondary">
                Changes may require Meta approval.
              </p>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={saving}
                  className="rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-xs font-semibold text-text-secondary transition hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>

                {/* <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center justify-center gap-2 rounded-lg bg-brand-main px-5 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:opacity-90 active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FiSave className="h-3.5 w-3.5" />
                  {saving ? "Saving..." : "Save changes"}
                </button> */}
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

/* Sub-components */

const FormSection = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) => (
  <section>
    <div className="mb-4">
      <h3 className="text-xs font-bold text-brand-secondary">{title}</h3>
      {description && (
        <p className="mt-0.5 text-[10px] text-text-secondary">{description}</p>
      )}
    </div>
    <div className="space-y-4">{children}</div>
  </section>
);

const FormField = ({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) => (
  <div>
    <div className="mb-1.5 flex items-center justify-between gap-2">
      <label className="text-[11px] font-semibold text-brand-secondary">
        {label}
      </label>
      {hint && <span className="text-[10px] text-gray-400">{hint}</span>}
    </div>
    {children}
  </div>
);

const IconInput = ({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div className="relative">
    <div className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-gray-400">
      {icon}
    </div>
    <div className="[&>input]:pl-9">{children}</div>
  </div>
);

const CharacterCount = ({ current, max }: { current: number; max: number }) => (
  <p className="mt-1 text-right text-[9px] text-gray-400">
    {current}/{max}
  </p>
);